import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { TabbedContainer, useAlert, useSimpleTabState } from "capitalrx-components-2.0/lib/general";
import { formatDate } from "capitalrx-utilities";

import { getMemberDocumentUrl, getPlanDocumentUrl, useListMemberDocumentsQuery, useListPlanDocumentsQuery } from "@/App/api/documents";
import { useListMemberDependentsQuery } from "@/App/api/member";
import { ContentPanel, PageWrapper, ThemedTabbedContainer, DependentDropdown } from "@/App/components";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { DocumentTab, DocumentTabDocument } from "./DocumentTab";
import { MemberDocumentRequest } from "@/App/types/documents";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { AuthenticationContext } from "@/App/context";
import { Stack } from "capitalrx-components-2.0/lib/layout";

const DOCUMENT_RESULTS_PER_PAGE = 50;

export const DocumentsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { data: rawPlanDocuments, isLoading: isLoadingPlanDocs } = useListPlanDocumentsQuery({ results_per_page: DOCUMENT_RESULTS_PER_PAGE });
  const { getAuthToken } = useContext(AuthenticationContext);
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const [selectedTabId, setSelectedTabId] = useSimpleTabState();
  const alert = useAlert();

  const [personCode, setPersonCode] = useState<string>(memberEligibility.member.person_code);

  const { data: rawMemberDocuments, isLoading: isLoadingMemberDocs } = useListMemberDocumentsQuery(personCode);

  const { data: dependents } = useListMemberDependentsQuery();

  const minorDependents = useMemo(() => {
      return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
    }, [dependents]);

  const hasDependents = useMemo(() =>
    !isViewingDataholder && minorDependents.length > 0, [isViewingDataholder, minorDependents]);

  const planDocuments: DocumentTabDocument[] = useMemo(() => {
    if (rawPlanDocuments) {
      return rawPlanDocuments.results.map((rawDoc) => {
        return {
          id: rawDoc.document.id,
          name: rawDoc.document.name,
          description: rawDoc.document.description,
          documentSource: "internal",
          documentType: rawDoc.document.document_type,
          submissionId: rawDoc.document.submission_id,
        };
      });
    } else {
      return [];
    }
  }, [rawPlanDocuments]);

  const memberDocuments: DocumentTabDocument[] = useMemo(() => {
    if (rawMemberDocuments) {
      return rawMemberDocuments.results.map((rawDoc) => {
        const source = rawDoc.document.external_document_id ? "external" : "internal";
        return {
          id: source === "external" ? rawDoc.document.external_document_id : rawDoc.document.id,
          name: source === "external" ? rawDoc.document.document_type : rawDoc.document.name,
          description: source === "external" ? formatDate(rawDoc.document.document_sent_date, "MMMM D, YYYY") : rawDoc.document.description,
          documentSource: source,
          documentType: rawDoc.document.document_type,
          submissionId: rawDoc.document.submission_id,
          submissionConfirmationNumber: rawDoc.document.submission_confirmation_number,
        };
      });
    } else {
      return [];
    }
  }, [rawMemberDocuments]);

  const onMemberDocDownload = useCallback(
    async (memberDoc: DocumentTabDocument) => {
      try {
        const downloadBody: MemberDocumentRequest = {
          "document_type": memberDoc.documentType,
        };
        if(memberDoc.documentSource === "external") {
          downloadBody.external_document_id = memberDoc.id.toString();
        } else {
          downloadBody.id = +memberDoc.id;
          downloadBody.submission_id = +memberDoc.submissionId;
        }
        const downloadResponse = await getMemberDocumentUrl(await getAuthToken(), downloadBody);
        logEvent(ANALYTIC_EVENTS.DOCUMENTS.download, { memberDocumentId: memberDoc.id, memberDocumentName: memberDoc.name });
        window.open(downloadResponse.url, "_parent");
      } catch (e) {
        console.error(e);
        alert({
          message: `${translate("DOCUMENTS_PAGE.couldNotDownloadDocumentAlert")}`,
          type: "error",
          style: "banner",
        });
      }
    },
    [memberDocuments],
  );

  const onPlanDocumentDownload = useCallback(
    async (planDoc: DocumentTabDocument) => {
      try {
        const downloadResponse = await getPlanDocumentUrl(await getAuthToken(), { id: planDoc.id.toString() });
        logEvent(ANALYTIC_EVENTS.DOCUMENTS.download, { memberDocumentId: planDoc.id.toString(), memberDocumentName: planDoc.name });
        window.open(downloadResponse.url, "_parent");
      } catch (e) {
        alert({
          message: `${translate("DOCUMENTS_PAGE.couldNotDownloadDocumentAlert")}`,
          type: "error",
          style: "banner",
        });
      }
    },
    [planDocuments],
  );

  const handleDependentFilterChange = (value: string) => {
    setPersonCode(value);
    logEvent(ANALYTIC_EVENTS.DOCUMENTS.dependentFilterChange, { personCode: value });
  };

  return (
    <PageWrapper>
      <ContentPanel title={translate("DOCUMENTS_PAGE.formsAndDocuments")} showBorder={false}>
        {hasDependents && (
          <Stack spacing="large">
            <DependentDropdown
              key="docs-dependents-view-dropdown"
              testId="docs-dependents-view-dropdown"
              onChange={handleDependentFilterChange}
              value={personCode}
              label={translate("DOCUMENTS_PAGE.viewDocumentsFor")}
              aria-label={translate("DOCUMENTS_PAGE.viewDocumentsFor")}
              dependents={minorDependents}
              member={memberEligibility.member}
            />
          </Stack>
        )}
        <ThemedTabbedContainer testId="documents-tab-container" selectedTab={selectedTabId} onSelectedTabChange={setSelectedTabId}>
          <TabbedContainer.TabPane key="member-doc-tab-container" testId="member-documents" id="member-documents" label={translate("DOCUMENTS_PAGE.memberDocuments")}>
            <DocumentTab key="member-doc-tab" isLoading={isLoadingMemberDocs} documents={memberDocuments} onDownload={onMemberDocDownload} />
          </TabbedContainer.TabPane>
          <TabbedContainer.TabPane key="plan-doc-tab-container" testId="plan-documents" id="plan-documents" label={translate("DOCUMENTS_PAGE.planDocuments")}>
            <DocumentTab key="plan-doc-tab" isLoading={isLoadingPlanDocs} documents={planDocuments} onDownload={onPlanDocumentDownload} />
          </TabbedContainer.TabPane>
        </ThemedTabbedContainer>
      </ContentPanel>
    </PageWrapper>
  );
};
